import { gql, useMutation } from '@apollo/client';
import * as React from 'react';

import logger from '../../lib/logger';
import Button from '../base/buttons/Button';
import Form from '../base/Form/Form';
import Input from '../base/Input';
import Modal from '../base/Modal';

import type { UpdateMerchantMutation, UpdateMerchantMutationVariables } from './__generated__/UpdateMerchantModal.gql';

const UPDATE_MERCHANT = gql`
  mutation UpdateMerchant($input: UpdateMerchantInput!) {
    updateMerchant(input: $input) {
      id
      name
    }
  }
`;

const MAX_NAME_LENGTH = 255;

interface IMerchant {
  id: string;
  name: string;
}

interface IBaseUpdateMerchantModalProps {
  merchant: IMerchant;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit: (data: { name: string }) => void;
  isLoading?: boolean;
}

export const BaseUpdateMerchantModal = ({
  merchant,
  isOpen,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: IBaseUpdateMerchantModalProps) => {
  const [name, setName] = React.useState(merchant.name);

  React.useEffect(() => {
    setName(merchant.name);
  }, [merchant]);

  const { isValid, errorMessage } = React.useMemo(() => {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      return {
        isValid: false,
        errorMessage: 'Name cannot be empty',
      };
    }
    if (trimmedName.length > MAX_NAME_LENGTH) {
      return {
        isValid: false,
        errorMessage: 'Name cannot be longer than 255 characters',
      };
    }
    return {
      isValid: true,
      errorMessage: '',
    };
  }, [name]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onSubmit({ name: name.trim() });
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setName(merchant.name);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} className="w-100">
      <Modal.Header onClose={handleClose}>
        <div className="flex flex-col gap-0.5">
          <div className="text-primary text-2xl font-semibold">Merchant details</div>
          <div className="text-tertiary text-sm font-normal">
            Updating the details below will affect how this merchant is displayed in the app.
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="flex-col items-start gap-5 px-6 pb-6 pt-0">
        <Form onSubmit={handleSubmit} className="w-full">
          <div className="flex w-full flex-col gap-8">
            <div className="flex w-full">
              <Input label="Name" value={name} onChange={setName} isInvalid={!isValid} hint={errorMessage} />
            </div>
            <div className="flex w-full gap-3">
              <Button color="secondary" onClick={handleClose} isDisabled={isLoading} className="flex-1" size="lg">
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                isLoading={isLoading}
                isDisabled={!isValid || isLoading}
                className="flex-1"
                size="lg"
              >
                Save details
              </Button>
            </div>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

interface IUpdateMerchantModalProps {
  merchant: IMerchant;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit?: (data: { name: string }) => void;
}

const UpdateMerchantModal = ({ merchant, isOpen, onOpenChange, onSubmit }: IUpdateMerchantModalProps) => {
  const [updateMerchant, { loading }] = useMutation<UpdateMerchantMutation, UpdateMerchantMutationVariables>(
    UPDATE_MERCHANT
  );

  const handleSubmit = async (data: { name: string }) => {
    try {
      await updateMerchant({
        variables: {
          input: {
            id: merchant.id,
            name: data.name,
          },
        },
      });

      onSubmit?.(data);
      onOpenChange(false);
    } catch (error) {
      logger.error('Failed to update merchant:', error);
    }
  };

  return (
    <BaseUpdateMerchantModal
      merchant={merchant}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      isLoading={loading}
    />
  );
};

export default UpdateMerchantModal;
